import { type HttpClient, type MultipartBody, query, segment } from '../http';

import { criteriaParams } from './games.params';
import {
  type AddToLibraryPayload,
  type CreateGamePayload,
  type GameExtension,
  type Game,
  type GameBarcodeResult,
  type GameSubmission,
  type UpdateGamePayload,
  type GameTaxonomies,
  type LibraryOverview,
  type LibraryScope,
  type ListGamesQuery,
  type ListGamesResult,
  type ListLibraryQuery,
  type ListLibraryResult,
  type RemovalReason,
  type RemovedLibraryItem,
} from './games.types';

/**
 * The catalogue, its taxonomies, and the personal or household library.
 *
 * @param http Transport supplied by the application.
 * @returns The `games` domain of the API client.
 */
export function createGamesApi(http: HttpClient) {
  return {
    /**
     * Reads the catalogue genres and mechanisms, plus the fixed enum options
     * with their display labels.
     *
     * Feeds both the library filters and the creation form's choice lists.
     *
     * @returns Every taxonomy the screens need, in one call.
     */
    /**
     * Lists the catalogue, which is also how it is searched.
     *
     * Warning: There is no `/games/search`. A `search` term is one criterion
     * among the others, so a screen offering search **and** filters has one
     * endpoint to reason about rather than two that must agree.
     *
     * @param params Criteria, sort and pagination.
     * @returns One page of games with the total.
     * @throws `game.error.filter.invalid_query` (400) on unreadable criteria.
     */
    /**
     * The sheets you offered to the catalogue, and where each one stands.
     *
     * ⚠️ Yours only, and it carries the reviewer's message. A notification is
     * read once; a correction has to be readable again while it is being made.
     */
    submissions: () => http.get<GameSubmission[]>('/games/submissions'),

    /**
     * Corrects a sheet.
     *
     * ⚠️ Only its creator may. And when the sheet was sent back for
     * corrections, saving is what returns it to the queue — the author has
     * nothing else to press, and nothing else would remind them to.
     *
     * @throws `game.error.manage.not_creator` (403).
     */
    update: (id: string, payload: UpdateGamePayload) =>
      http.patch<Game>(`/games/${segment(id)}`, payload),

    list: (params: ListGamesQuery = {}) =>
      http.get<ListGamesResult>(
        `/games${query({
          ...criteriaParams(params),
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    getTaxonomies: () => http.get<GameTaxonomies>('/games/taxonomies'),

    /**
     * Reads one game in full.
     *
     * @param id The game id.
     * @returns The catalogue entry, with the reader's ownership flags.
     * @throws 404 when no game has that id.
     */
    findOne: (id: string) => http.get<Game>(`/games/${segment(id)}`),

    /**
     * Looks a barcode up: our catalogue first, then outside.
     *
     * Warning: **Always answers 200.** An unknown barcode is `found: false`,
     * not an error — a screen that treats it as one shows a failure where the
     * honest answer is "nobody knows this box". Read `found` first, then
     * `existingGameId` to tell "we have it" from "we could prefill a sheet".
     *
     * @param ean 8 to 14 digits — EAN-8, UPC-A, EAN-13 or GTIN-14.
     * @returns What is known of that barcode.
     * @throws `game.error.barcode.invalid_format` (400) on anything else.
     */
    findByBarcode: (ean: string) =>
      http.get<GameBarcodeResult>(`/games/barcode/${segment(ean)}`),

    /**
     * Lists the caller's library, or their household's.
     *
     * Accepts the same criteria as the catalogue, plus `neverPlayed` and
     * `sharedOnly`, which describe owning rather than the game itself.
     *
     * @param params Scope, filters, sort and pagination; defaults to `mine`.
     * @returns One page of entries, one row per game.
     * @throws `game.error.list_library.not_in_family` (400) — `scope: 'family'` without a household.
     * @throws `game.error.list_library.shared_only_outside_family` (400) —
     *   `sharedOnly` asked of the personal shelf, which holds none.
     */
    listLibrary: (params: ListLibraryQuery = {}) =>
      http.get<ListLibraryResult>(
        `/games/library${query({
          ...criteriaParams(params),
          scope: params.scope,
          neverPlayed: params.neverPlayed,
          sharedOnly: params.sharedOnly,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          page: params.page,
          limit: params.limit,
        })}`,
      ),

    /**
     * Reads the collection overview: game count, value, backlog, plays,
     * average duration, favourite genre and the genre breakdown.
     *
     * Warning: Takes **only** the scope, deliberately not the list's criteria: it
     * describes the collection, the list describes the search.
     *
     * @param scope Whose collection to describe; defaults to `mine`.
     * @returns The aggregates, all computed from one snapshot.
     * @throws `game.error.list_library.not_in_family` (400) — `family` without a household.
     */
    getLibraryOverview: (scope?: LibraryScope) =>
      http.get<LibraryOverview>(`/games/library/overview${query({ scope })}`),

    /**
     * A base game's extensions, each annotated with who in the reader's circle
     * owns it.
     *
     * @param id The base game.
     * @returns Its extensions, oldest copy first.
     * @throws `game.error.access.not_found` (404) — no base game with this id.
     */
    getExtensions: (id: string) =>
      http.get<GameExtension[]>(`/games/${segment(id)}/extensions`),

    /**
     * Creates a game sheet.
     *
     * Warning: private unless `visibility: 'PUBLIC'` is asked for, and
     * publishing needs an `ean`.
     *
     * @param payload What the form collected.
     * @returns The game as it now stands.
     * @throws `game.error.create.ean_required` (400) — publishing with no barcode.
     * @throws `game.error.create.parent_not_found` (400) — the base game does not exist.
     */
    create: (payload: CreateGamePayload) => http.post<Game>('/games', payload),

    /**
     * Uploads a cover for a game sheet.
     *
     * The server re-encodes it and answers the URL to put in `imageUrl` — the
     * bytes that were sent are never served back, and neither is the file name
     * they came under.
     *
     * @param body Multipart body carrying the file under the `file` key.
     * @returns The public URL of the stored cover.
     * @throws `game.error.image.not_an_image` (400) — the bytes are not a decodable image.
     * @throws `game.error.image.too_large` (413) — over 5 MB.
     */
    uploadImage: (body: MultipartBody) =>
      http.post<{ url: string }>('/uploads/game-image', body),

    /**
     * Adds a catalogue game to the library.
     *
     * @param payload The game, and whether it is a joint purchase.
     * @throws `game.error.add_to_library.already_owned` (409) — the game is already there.
     */
    addToLibrary: (payload: AddToLibraryPayload) =>
      http.post<void>('/games/library', payload),

    /**
     * Removes one entry from the library.
     *
     * Warning: Takes the id of the **entry** (`LibraryItem.id`), not of the game: the
     * same game can be owned by several members of a household.
     *
     * @param libraryItemId The entry to remove.
     */
    removeFromLibrary: (libraryItemId: string) =>
      http.delete<void>(`/games/library/${segment(libraryItemId)}`),

    /**
     * Takes several games off a shelf in one gesture.
     *
     * ⚠️ Takes the entries' ids, not the games'. One statement on the server,
     * filtered by ownership: half a selection can never go while the other
     * half stays, and an id that is not the caller's simply does not match.
     *
     * @param ids The entries to remove.
     * @param reason Why they left, when the reader said. ⚠️ Optional, and it
     * stays optional — somebody clearing out five boxes wants them gone.
     * @returns How many actually left, which the undo banner counts.
     */
    removeManyFromLibrary: (ids: string[], reason?: RemovalReason) =>
      http.post<{ removed: number }>('/games/library/remove', { ids, reason }),

    /**
     * Says why entries already off a shelf left it.
     *
     * ⚠️ What « Préciser » calls. Somebody who switched the confirmation off
     * is never asked at removal time, and this is the only way back to the
     * answer — the checkbox disarms the warning, never the reason.
     *
     * @param ids The removed entries. Ids still on a shelf do not match, so
     * this can never take anything off one.
     * @param reason Why they left. Absent clears it, which is how an answer
     * given by mistake is taken back.
     * @returns How many were annotated.
     */
    setRemovalReason: (ids: string[], reason?: RemovalReason) =>
      http.patch<{ updated: number }>('/games/library/removed/reason', {
        ids,
        reason,
      }),

    /**
     * The games taken off this shelf, newest first.
     *
     * ⚠️ Kept indefinitely, which is what makes the confirmation safe to
     * switch off: a removal regretted the following week is one click away.
     */
    listRemovedFromLibrary: () =>
      http.get<RemovedLibraryItem[]>('/games/library/removed'),

    /**
     * Puts one entry back on the shelf.
     *
     * @param libraryItemId The entry to restore.
     */
    restoreToLibrary: (libraryItemId: string) =>
      http.post<void>(`/games/library/${segment(libraryItemId)}/restore`),

    /**
     * Deletes a custom game from the catalogue.
     *
     * ⚠️ Not the same gesture as `removeFromLibrary`, and the difference is
     * felt by strangers rather than by the caller. This asks the catalogue to
     * forget the entry: the server erases it outright when nothing outside the
     * caller's own rows points at it, and hides it otherwise — a cascade would
     * take the game off somebody else's shelf in silence. Which of the two
     * happened is not reported, because the game is gone from every screen
     * either way.
     *
     * Warning: only a custom game, and only its creator or a platform admin.
     * Catalogue entries answer `game.error.access.catalog_read_only`.
     *
     * @param gameId The game to delete.
     */
    remove: (gameId: string) => http.delete<void>(`/games/${segment(gameId)}`),
  };
}

export type GamesApi = ReturnType<typeof createGamesApi>;
